import React, { useEffect, useRef, useState, useCallback } from "react";
import { Upload, Link2, RotateCcw, Image as ImageIcon, Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { MEDIA_SLOTS, mediaUrl } from "../../lib/siteMedia";

export const SitePhotosTab = () => {
  const [media, setMedia] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/site-media");
      setMedia(r.data);
    } catch (e) { toast.error("Failed to load site photos"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="text-[var(--twr-warm-white)]/55">Loading…</p>;

  const groups = [...new Set(MEDIA_SLOTS.map((s) => s.group))];

  return (
    <div data-testid="admin-photos-tab" className="space-y-12">
      <div>
        <h1 className="font-serif text-4xl">Site Photos</h1>
        <p className="text-[var(--twr-warm-white)]/55 mt-2">Update the photos shown on the home page — upload an image or paste an image URL. Changes are live immediately.</p>
      </div>
      {groups.map((g) => (
        <div key={g}>
          <p className="eyebrow text-[var(--twr-bronze)] flex items-center gap-2"><ImageIcon size={13} /> {g}</p>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MEDIA_SLOTS.filter((s) => s.group === g).map((slot) => (
              <PhotoSlot key={slot.key} slot={slot} media={media} onChanged={load} />
            ))}
          </div>
        </div>
      ))}

      <GallerySection />
    </div>
  );
};

function GallerySection() {
  const fileRef = useRef(null);
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/gallery");
      setImages(r.data);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addByUrl = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/gallery", { url: url.trim() });
      toast.success("Photo added to gallery");
      setUrl("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add"); }
    finally { setBusy(false); }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Image must be under 4 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/gallery", { url: r.data.url });
      toast.success("Photo uploaded to gallery");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (g) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    await api.delete(`/admin/gallery/${g.id}`);
    toast.success("Photo removed");
    load();
  };

  return (
    <div data-testid="admin-gallery-section">
      <p className="eyebrow text-[var(--twr-bronze)] flex items-center gap-2"><ImageIcon size={13} /> Gallery Page ({images.length} photos)</p>
      <div className="mt-5 flex flex-wrap gap-3 items-center">
        <input ref={fileRef} type="file" accept="image/*" onChange={uploadFile} className="hidden" data-testid="gallery-upload-input" />
        <button disabled={busy} onClick={() => fileRef.current?.click()} data-testid="gallery-upload-btn" className="btn-bronze">
          <Plus size={14} /> {busy ? "Working…" : "Upload Photo"}
        </button>
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="or paste an https:// image URL"
          data-testid="gallery-url-input"
          className="flex-1 min-w-[240px] bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-2.5 text-sm" />
        <button disabled={busy || !url.trim()} onClick={addByUrl} data-testid="gallery-url-add" className="btn-ghost">Add URL</button>
      </div>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {images.map((g) => (
          <div key={g.id} data-testid={`gallery-admin-item-${g.id}`} className="relative group aspect-square overflow-hidden surface">
            <img src={g.url} alt="" className="w-full h-full object-cover" />
            <button onClick={() => remove(g)} data-testid={`gallery-delete-${g.id}`} title="Remove"
              className="absolute top-2 right-2 inline-flex items-center justify-center w-8 h-8 bg-black/70 border border-[var(--twr-line-strong)] text-[var(--twr-warm-white)] hover:border-red-400 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        {images.length === 0 && <p className="text-[var(--twr-warm-white)]/55 col-span-full py-6">No gallery photos yet.</p>}
      </div>
    </div>
  );
}

function PhotoSlot({ slot, media, onChanged }) {
  const fileRef = useRef(null);
  const [urlMode, setUrlMode] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(0);
  const overridden = !!media[slot.key];
  const src = mediaUrl(media, slot.key);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Image must be under 4 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/admin/site-media/${slot.key}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${slot.label} updated`);
      setBust(Date.now());
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveUrl = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await api.put(`/admin/site-media/${slot.key}`, { url: url.trim() });
      toast.success(`${slot.label} updated`);
      setUrl(""); setUrlMode(false);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save URL"); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset this photo to the original?")) return;
    await api.delete(`/admin/site-media/${slot.key}`);
    toast.success("Reset to original");
    onChanged();
  };

  return (
    <div className="surface overflow-hidden" data-testid={`photo-slot-${slot.key}`}>
      <div className="aspect-[4/3] overflow-hidden bg-[#0A0A0A]">
        <img src={src.startsWith(process.env.REACT_APP_BACKEND_URL) ? `${src}?v=${bust}` : src} alt={slot.label} className="w-full h-full object-cover" />
      </div>
      <div className="p-4">
        <p className="text-[13px] leading-snug">{slot.label}</p>
        <p className="text-[11px] tracking-[0.18em] uppercase mt-1 text-[var(--twr-warm-white)]/45">{overridden ? "Custom" : "Original"}</p>
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid={`photo-upload-input-${slot.key}`} />
        {!urlMode ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <button disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`photo-upload-btn-${slot.key}`}
              className="inline-flex items-center gap-1.5 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] transition-colors px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase">
              <Upload size={11} /> {busy ? "…" : "Upload"}
            </button>
            <button disabled={busy} onClick={() => setUrlMode(true)} data-testid={`photo-url-btn-${slot.key}`}
              className="inline-flex items-center gap-1.5 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] transition-colors px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase">
              <Link2 size={11} /> URL
            </button>
            {overridden && (
              <button disabled={busy} onClick={reset} data-testid={`photo-reset-btn-${slot.key}`} title="Reset to original"
                className="inline-flex items-center gap-1.5 border border-[var(--twr-line-strong)] hover:border-red-400 hover:text-red-400 transition-colors px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase">
                <RotateCcw size={11} /> Reset
              </button>
            )}
          </div>
        ) : (
          <div className="mt-3 flex gap-2">
            <input autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…"
              data-testid={`photo-url-input-${slot.key}`}
              className="flex-1 min-w-0 bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-2 py-1.5 text-[12px]" />
            <button disabled={busy} onClick={saveUrl} data-testid={`photo-url-save-${slot.key}`} className="btn-bronze !px-3 !py-1.5 text-[11px]">Save</button>
            <button onClick={() => { setUrlMode(false); setUrl(""); }} className="text-[var(--twr-warm-white)]/55 text-[11px] px-1">✕</button>
          </div>
        )}
      </div>
    </div>
  );
}
